import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

/**
 * Gift Card Form Component
 * Handles the gift card recipient form with multi-step navigation
 * 
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLFormElement} form - The form element
 * @property {HTMLElement} recipientStep - Step 1 container
 * @property {HTMLElement} giftNoteStep - Step 2 container
 * @property {HTMLButtonElement} recipientTab - Tab for step 1
 * @property {HTMLButtonElement} giftNoteTab - Tab for step 2
 * @property {HTMLButtonElement} continueBtn - Continue button
 * @property {HTMLButtonElement} backBtn - Back button
 * @property {HTMLInputElement} sendToMyself - Send to myself checkbox
 * @property {HTMLButtonElement} sendNowBtn - Send now button
 * @property {HTMLButtonElement} sendLaterBtn - Send later button
 * @property {HTMLElement} datePicker - Date picker container
 * @property {HTMLButtonElement} submitBtn - Submit button
 * 
 * @extends Component<Refs>
 */
class GiftCardForm extends Component {
  requiredRefs = ['dialog'];

  constructor() {
    super();
    this.currentStep = 1;
    this.sendNow = true;
  }

  connectedCallback() {
    super.connectedCallback();
    this.#bindEvents();

    // Listen for variant updates to keep the hidden variant id in sync
    /** @type {(event: CustomEvent) => void} */
    this._onVariantUpdate = (event) => this.#handleVariantUpdate(event);
    document.addEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
  }

  disconnectedCallback() {
    // @ts-ignore - parent class may not implement disconnectedCallback
    super.disconnectedCallback?.();

    if (this._onVariantUpdate) {
      document.removeEventListener(ThemeEvents.variantUpdate, this._onVariantUpdate);
    }
  }

  #bindEvents() {
    // Step navigation
    this.refs.continueBtn?.addEventListener('click', () => this.#goToStep(2));
    this.refs.backBtn?.addEventListener('click', () => this.#goToStep(1));
    this.refs.recipientTab?.addEventListener('click', () => this.#goToStep(1));
    this.refs.giftNoteTab?.addEventListener('click', () => {
      if (this.#validateStep1()) this.#goToStep(2);
    });

    // Send to myself checkbox
    this.refs.sendToMyself?.addEventListener('change', (e) => {
      this.#handleSendToMyself(/** @type {HTMLInputElement} */ (e.target).checked);
    });

    // Delivery date toggle
    this.refs.sendNowBtn?.addEventListener('click', () => this.#setDeliveryOption(true));
    this.refs.sendLaterBtn?.addEventListener('click', () => this.#setDeliveryOption(false));

    // Form submission
    this.refs.submitBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#handleSubmit();
    });
  }

  /**
   * Opens the dialog
   */
  showDialog() {
    const { dialog } = this.refs;
    if (!dialog || dialog.open) return;

    dialog.showModal();
    this.#goToStep(1);
    this.dispatchEvent(new CustomEvent('dialog:open'));
  }

  /**
   * Closes the dialog with animation
   */
  async closeDialog() {
    const { dialog } = this.refs;
    if (!dialog || !dialog.open) return;

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new CustomEvent('dialog:close'));
  }

  /**
   * Navigate to a specific step
   * @param {number} step
   */
  #goToStep(step) {
    if (step === 2 && !this.#validateStep1()) {
      return;
    }

    this.currentStep = step;
    
    // Update tabs
    this.refs.recipientTab?.classList.toggle('active', step === 1);
    this.refs.giftNoteTab?.classList.toggle('active', step === 2);
    
    // Update steps visibility
    this.refs.recipientStep?.classList.toggle('hidden', step !== 1);
    this.refs.giftNoteStep?.classList.toggle('hidden', step !== 2);
    
    // Update buttons
    this.refs.continueBtn?.classList.toggle('hidden', step !== 1);
    this.refs.backBtn?.classList.toggle('hidden', step !== 2);
    this.refs.submitBtn?.classList.toggle('hidden', step !== 2);
  }

  /**
   * Validate step 1 fields
   * When "Send to myself" is checked, recipient name and email are not required.
   * @returns {boolean}
   */
  #validateStep1() {
    const sendToMyself = this.refs.sendToMyself?.checked ?? false;
    const recipientName = /** @type {HTMLInputElement} */ (this.querySelector('[name="recipient_name"]'));
    const recipientEmail = /** @type {HTMLInputElement} */ (this.querySelector('[name="recipient_email"]'));
    const senderName = /** @type {HTMLInputElement} */ (this.querySelector('[name="sender_name"]'));
    
    let valid = true;

    // Sender name is always required
    if (!senderName?.value?.trim()) {
      senderName?.classList.add('field--error');
      valid = false;
    } else {
      senderName?.classList.remove('field--error');
    }

    // Recipient name and email only required when not sending to myself
    if (!sendToMyself) {
      if (!recipientName?.value?.trim()) {
        recipientName?.classList.add('field--error');
        valid = false;
      } else {
        recipientName?.classList.remove('field--error');
      }
      if (!recipientEmail?.value?.trim()) {
        recipientEmail?.classList.add('field--error');
        valid = false;
      } else {
        recipientEmail?.classList.remove('field--error');
      }
      // Validate email format when provided
      if (recipientEmail?.value && !this.#isValidEmail(recipientEmail.value)) {
        recipientEmail.classList.add('field--error');
        valid = false;
      }
    } else {
      recipientName?.classList.remove('field--error');
      recipientEmail?.classList.remove('field--error');
    }

    return valid;
  }

  /**
   * Validate email format
   * @param {string} email
   * @returns {boolean}
   */
  #isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Handle send to myself checkbox
   * @param {boolean} checked
   */
  #handleSendToMyself(checked) {
    const recipientFields = this.querySelectorAll('.recipient-field');
    recipientFields.forEach(field => {
      field.classList.toggle('hidden', checked);
    });

    if (checked) {
      // Pre-fill with customer info if available
      // @ts-ignore
      const customerEmail = window.customerEmail || '';
      // @ts-ignore
      const customerName = window.customerName || '';
      
      const recipientName = /** @type {HTMLInputElement} */ (this.querySelector('[name="recipient_name"]'));
      const recipientEmail = /** @type {HTMLInputElement} */ (this.querySelector('[name="recipient_email"]'));
      
      if (recipientName) recipientName.value = customerName;
      if (recipientEmail) recipientEmail.value = customerEmail;
    }
  }

  /**
   * Set delivery option
   * @param {boolean} sendNow
   */
  #setDeliveryOption(sendNow) {
    this.sendNow = sendNow;
    
    this.refs.sendNowBtn?.classList.toggle('active', sendNow);
    this.refs.sendLaterBtn?.classList.toggle('active', !sendNow);
    this.refs.datePicker?.classList.toggle('hidden', sendNow);

    // Set minimum date to tomorrow
    if (!sendNow && this.refs.datePicker) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const dateInput = this.refs.datePicker.querySelector('input');
      if (dateInput) {
        dateInput.setAttribute('min', tomorrow.toISOString().split('T')[0]);
      }
    }
  }

  /**
   * Handle form submission
   */
  #handleSubmit() {
    const formData = new FormData(this.refs.form);
    
    // Add gift card properties
    const properties = {
      'Recipient Name': formData.get('recipient_name'),
      'Recipient Email': formData.get('recipient_email'),
      'Sender Name': formData.get('sender_name'),
      'Gift Note': formData.get('gift_note') || '',
      'Delivery': this.sendNow ? 'Send Now' : String(formData.get('delivery_date') || '')
    };

    const variantId = /** @type {HTMLInputElement} */ (this.querySelector('[name="id"]'))?.value;
    
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Add to cart with properties
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties
      })
    })
      .then(response => response.json())
      .then(() => {
        this.closeDialog();
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        // Open cart drawer if available (no redirect to cart page)
        const cartDrawer = document.querySelector('cart-drawer-component');
        // @ts-ignore
        cartDrawer?.open?.();
      })
    .catch(error => {
      console.error('Error adding to cart:', error);
      alert('There was an error adding the gift card to your cart. Please try again.');
    });
  }

  /**
   * Sync selected variant id when the product variant changes
   * @param {CustomEvent} event
   */
  #handleVariantUpdate(event) {
    const detail = event.detail;
    if (!detail || !detail.data || !detail.data.productId) return;

    const productId = this.dataset.productId;
    if (!productId || detail.data.productId !== productId) return;

    const newVariantId = detail.resource && detail.resource.id;
    if (!newVariantId) return;

    const idInput = /** @type {HTMLInputElement} */ (this.querySelector('[name="id"]'));
    if (idInput) {
      idInput.value = newVariantId;
    }
  }
}

if (!customElements.get('gift-card-form')) {
  customElements.define('gift-card-form', GiftCardForm);
}
